import { styled } from "@linaria/react";
import React from "react";
import type { Project } from "../../../api/useProjects";
import type { Source } from "../../../api/useSources";
import { useCanUserDoOnProject } from "../../canUserDo";
import t from "../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import ChartCard from "@ui/Highcharts/ui/Card";
import Switch from "@ui/Switch";
import ActionMenu from "../Analysis/AnalysisActionMenu";
import PeriodSelector from "../Analysis/PeriodSelector";
import cx from "classnames";
import ToggleGroupBreaks from "../Analysis/ToggleGroupBreaks";
import { useDateRange } from "../Analysis/useSelectedAnalyses";
import { commonLegendactiveSetup } from "../Chart/Legend/CommonLegend";
import type { SelectedQuantity } from "../useSelectedQuantities";
import GroupUnitInput, { useGroupUnitState } from "./GroupUnitInput";
import { fonts } from "@ui";
import type { Analysis } from "../../../api/Analysis";
import type { MeasuresChartType } from "./MeasuresChartType";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import TogglePublicationDisplay, {
  usePublicationFlagDisplay,
} from "../Analysis/TogglePublicationDisplay";
import { ChartMenu } from "../Chart/Menu";
import { useBreaksBetweenGroups } from "../Analysis/useBreaksBetweenGroups";
import { FullViewportButtonMarker, FullViewportTrigger } from "@ui/FullViewportButton";
import { useSettings } from "../../../api/useSettings";
import ToggleDisplayInvalidPoints, {
  useDisplayInvalidPoints,
} from "../Analysis/ToggleDisplayInvalidPoints";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { useSetups } from "../../../api/useSetups";
import { useMetrologyOverrides } from "../../../api/useMetrologyOverrides";
import { useProjectPeriod } from "../useProjectPeriod";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";
import useActiveAnalysesWithDisplayFilters from "../../../api/useActiveAnalysesWithDisplayFilters";
import MeasureHighcharts from "./MeasureHighcharts";
import { hasAnalysisQuantityData, useHasDataInPeriod } from "../useNoDataIntervals";
import PeriodZoomAllButton from "../Chart/PeriodZoomAllButton";
import EvolutionSubHeader from "../EvolutionSubHeader";
import InfoTooltip from "../Chart/InfoTooltip";
import Selector from "@ui/Selector";
import { DisplayFiltersBlankState } from "../EvolutionSubHeader/DisplayFiltersBlankState";

const Description = styled.div`
  font: ${fonts.body2};
  margin-left: auto;
`;

interface P {
  project: Project;
  analyses: Analysis[];
  activeAnalyses: ActiveAnalysis[];
  rawActiveAnalysesCount: number;
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  samplingDescription: string | null;
  canUpdateQuantityValues: boolean;
  selectedChartOption: MeasuresChartType;
  setSelectedChartOption: (option: MeasuresChartType) => void;
  allAnalysesMode: boolean;
  setAllAnalysesMode: (v: React.SetStateAction<boolean>) => void;
}

const MeasuresEvolution = ({
  project,
  source,
  analyses,
  activeAnalyses,
  rawActiveAnalysesCount,
  selectedQuantities,
  samplingDescription,
  canUpdateQuantityValues,
  selectedChartOption,
  setSelectedChartOption,
  allAnalysesMode,
  setAllAnalysesMode,
}: P) => {
  const projectId = project.id;

  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);
  const displayFilterRemovesAllActiveAnalysesInCurrentMode =
    allAnalysesMode === false && displayFilterRemovesAllActiveAnalyses;

  const setups = useSetups(projectId);
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const [dateRange] = useDateRange({
    scope: projectId,
  });
  const settings = useSettings();
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);

  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: projectId,
  });
  const [publicationFlagDisplay] = usePublicationFlagDisplay({
    scope: projectId,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });
  const [axisGroupedByUnit] = useGroupUnitState({
    scope: projectId,
  });

  const canChangeSampleMode = Boolean(activeAnalyses.length);

  //action allowed if udpdate, delete permission

  const displayGroupUnitInput = React.useMemo(
    () => selectedQuantities.length > 1,
    [selectedQuantities],
  );

  const [period, setPeriod, extremes] = useProjectPeriod(
    allAnalysesMode
      ? {
          source,
          project,
          boundaries: "analyses",
        }
      : {
          source,
          project,
          boundaries: "active-analyses",
          analyses: activeAnalyses,
        },
  );

  const hasDataFn = React.useCallback(
    (analysis: Analysis | ActiveAnalysis) =>
      hasAnalysisQuantityData(
        analysis,
        selectedQuantities.map(q => q.quantity),
        selectedChartOption,
        setups,
        metrologyOverrides,
        displayInvalidPoints,
      ),
    [selectedQuantities, selectedChartOption, setups, metrologyOverrides, displayInvalidPoints],
  );

  const hasDataInPeriodActive = useHasDataInPeriod(activeAnalyses, period, hasDataFn);
  const hasDataInPeriodAll = useHasDataInPeriod(analyses, period, hasDataFn);

  const hasDataInPeriod = allAnalysesMode ? hasDataInPeriodAll : hasDataInPeriodActive;

  const hasDataInAllPeriodActive = useHasDataInPeriod(activeAnalyses, extremes, hasDataFn);
  const hasDataInAllPeriodAll = useHasDataInPeriod(analyses, extremes, hasDataFn);

  const hasDataInAllPeriod = allAnalysesMode ? hasDataInAllPeriodAll : hasDataInAllPeriodActive;

  const handleZoomToAllPeriod = React.useCallback(
    () => setPeriod({ from: extremes.from, to: extremes.to }),
    [extremes.from, extremes.to, setPeriod],
  );

  const setup =
    dateRange.pivot === null ? null : getAnalysisSetup(setups, { date: dateRange.pivot });

  const legendLeft = React.useMemo(
    () =>
      setup ? (
        <div
          className={cx(commonLegendactiveSetup, HIDE_ON_WIDGET_EXPORT)}
          title={t("project.measure.legend_right.active_setup.name", setup.name)}
        >
          {t("project.measure.legend_right.active_setup.name", setup.name)}
        </div>
      ) : null,
    [setup],
  );

  const someQuantityStdAvailable = React.useMemo(
    () => selectedQuantities.some(value => value.quantity.formula === null),
    [selectedQuantities],
  );

  const chartTypeOptions = React.useMemo(
    () => [
      {
        value: "value" as const,
        label: t("measures.chart.toggle.byValue"),
        description: t("measures.chart.toggle.byValue.description"),
      },
      {
        value: "std" as const,
        label: t("measures.chart.toggle.byStd"),
        description: t("measures.chart.toggle.byStd.description"),
        disabled: !someQuantityStdAvailable,
      },
    ],
    [someQuantityStdAvailable],
  );

  React.useEffect(() => {
    if (!selectedQuantities.some(value => value.quantity.formula === null))
      setSelectedChartOption("value");
  }, [selectedQuantities, setSelectedChartOption]);

  const handleSwitchallAnalysesMode = React.useCallback(
    () => setAllAnalysesMode(allAnalysesMode => !allAnalysesMode),
    [setAllAnalysesMode],
  );

  const title = React.useMemo(
    () => (
      <>
        <Text as="span" type="h4">
          {t("measures.chart.title")}
        </Text>
        {rawActiveAnalysesCount > 0 ? (
          <Switch
            checked={allAnalysesMode}
            disabled={rawActiveAnalysesCount === 0}
            onChange={handleSwitchallAnalysesMode}
            className={HIDE_ON_WIDGET_EXPORT}
          >
            {t("measures.chart.title.switch")}
          </Switch>
        ) : null}
        <div style={{ marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("measures.chart.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
        </div>
      </>
    ),
    [allAnalysesMode, handleSwitchallAnalysesMode, project.name, rawActiveAnalysesCount],
  );

  const subtitle = React.useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={projectId}
        allAnalysesMode={allAnalysesMode}
        chartOptions={
          <InfoTooltip
            title={t("symptom.chart.header.option.title")}
            options={chartTypeOptions}
            link="/symptoms-page"
          >
            <div className={HIDE_ON_WIDGET_EXPORT}>
              <Selector
                options={chartTypeOptions}
                value={selectedChartOption}
                onChange={setSelectedChartOption}
              />
            </div>
          </InfoTooltip>
        }
        periodSettings={
          <>
            <PeriodZoomAllButton
              visible={
                !displayFilterRemovesAllActiveAnalysesInCurrentMode &&
                !hasDataInPeriod &&
                hasDataInAllPeriod
              }
              onClick={handleZoomToAllPeriod}
              label={t("chart.period.zoom_all")}
            />
            {allAnalysesMode === true ? (
              <PeriodSelector
                source={source}
                project={project}
                analyses={undefined}
                boundaries="analyses"
              />
            ) : (
              <PeriodSelector
                source={source}
                project={project}
                analyses={activeAnalyses}
                boundaries="active-analyses"
              />
            )}
          </>
        }
        visibilitySettings={
          <ChartMenu
            marker={
              publicationFlagDisplay ||
              !breakBetweenGroups ||
              displayInvalidPoints ||
              (axisGroupedByUnit && displayGroupUnitInput)
            }
            tooltipOverlay={t("chart.display_options.tooltip")}
          >
            <ToggleDisplayInvalidPoints projectId={projectId} />
            {displayGroupUnitInput ? <GroupUnitInput projectId={projectId} /> : null}
            {canChangeSampleMode ? (
              <TogglePublicationDisplay allAnalysesMode={allAnalysesMode} projectId={projectId} />
            ) : null}
            <ToggleGroupBreaks projectId={projectId} groupables={analyses} />
          </ChartMenu>
        }
        actionMenu={
          displayFilterRemovesAllActiveAnalysesInCurrentMode ? null : (
            <div className={wrapperFilterSelector}>
              <ActionMenu
                event={null}
                analyses={analyses}
                project={project}
                canWriteProject={canUserWriteProject}
                canUserReadProject={canUserReadProject}
                allAnalysesMode={allAnalysesMode}
                className={HIDE_ON_WIDGET_EXPORT}
              />
              {samplingDescription ? (
                <Description className={HIDE_ON_WIDGET_EXPORT}>{samplingDescription}</Description>
              ) : null}
            </div>
          )
        }
      />
    ),
    [
      projectId,
      allAnalysesMode,
      chartTypeOptions,
      selectedChartOption,
      setSelectedChartOption,
      displayFilterRemovesAllActiveAnalysesInCurrentMode,
      hasDataInPeriod,
      hasDataInAllPeriod,
      handleZoomToAllPeriod,
      source,
      project,
      activeAnalyses,
      publicationFlagDisplay,
      breakBetweenGroups,
      displayInvalidPoints,
      axisGroupedByUnit,
      displayGroupUnitInput,
      canChangeSampleMode,
      analyses,
      canUserWriteProject,
      canUserReadProject,
      samplingDescription,
    ],
  );

  if (displayFilterRemovesAllActiveAnalysesInCurrentMode)
    return (
      <ChartCard
        title={title}
        subtitle={subtitle}
        legendLeft={legendLeft}
        className={cx(measureChartCard, FullViewportButtonMarker, exportWidgetTarget)}
      >
        <DisplayFiltersBlankState projectId={projectId} />
      </ChartCard>
    );

  return (
    <MeasureHighcharts
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      project={project}
      analyses={analyses}
      activeAnalyses={activeAnalyses}
      source={source}
      selectedQuantities={selectedQuantities}
      canUpdateQuantityValues={canUpdateQuantityValues}
      selectedChartOption={selectedChartOption}
      allAnalysesMode={allAnalysesMode}
    />
  );
};

export default React.memo(MeasuresEvolution);

//#region CSS
const measureChartCard = css`
  min-height: 600px;
  row-gap: ${spacings.m};
`;

const wrapperFilterSelector = css`
  display: flex;
  align-items: center;
  gap: ${spacings.l};
`;
//#endregion CSS
